import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { chimneyService } from '../services/chimneyService';
import Layout from '../components/Layout';

const AdminPanel = () => {
  const { user, isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();
  const [chimneys, setChimneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingChimney, setEditingChimney] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    city: '',
    phone: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || !isAdmin()) {
      navigate('/');
      return;
    }
    fetchChimneys();
  }, [user, isAdmin, navigate]);

  const fetchChimneys = async () => {
    try {
      setLoading(true);
      const data = await chimneyService.getAllPages();
      setChimneys(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Błąd ładowania kominiarzy:', err);
      setError('Nie udało się załadować listy kominiarzy');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (chimney) => {
    setEditingChimney(chimney.id);
    setFormData({
      company_name: chimney.company_name || '',
      city: chimney.city || '',
      phone: chimney.phone || '',
      description: chimney.description || '',
    });
  };

  const handleCancel = () => {
    setEditingChimney(null);
    setFormData({
      company_name: '',
      city: '',
      phone: '',
      description: '',
    });
  };

  const handleSave = async (id) => {
    try {
      setSaving(true);
      await chimneyService.update(id, formData);
      await fetchChimneys();
      setEditingChimney(null);
      setFormData({
        company_name: '',
        city: '',
        phone: '',
        description: '',
      });
      alert('Dane kominiarza zostały zaktualizowane!');
    } catch (err) {
      console.error('Błąd aktualizacji:', err);
      alert(
        err.response?.data?.detail ||
          'Nie udało się zaktualizować danych kominiarza'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-ik-orange text-xl">Ładowanie...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-ik-black mb-6">
            Panel Administratora - Zarządzanie Kominiarzami
          </h1>

          {error && (
            <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nazwa firmy
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Miasto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Telefon
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Opis
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Akcje
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {chimneys.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                        Brak kominiarzy w systemie
                      </td>
                    </tr>
                  ) : (
                    chimneys.map((chimney) => (
                      <tr key={chimney.id}>
                        {editingChimney === chimney.id ? (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {chimney.id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="text"
                                name="company_name"
                                value={formData.company_name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange text-sm"
                                placeholder="Nazwa firmy"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange text-sm"
                                placeholder="Miasto"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange text-sm"
                                placeholder="Telefon"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {chimney.email || '-'}
                            </td>
                            <td className="px-6 py-4">
                              <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows="2"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange text-sm"
                                placeholder="Opis"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => handleSave(chimney.id)}
                                disabled={saving}
                                className="text-ik-orange hover:text-orange-600 mr-3 disabled:opacity-50"
                              >
                                {saving ? 'Zapisywanie...' : 'Zapisz'}
                              </button>
                              <button
                                onClick={handleCancel}
                                className="text-gray-600 hover:text-gray-800 disabled:opacity-50"
                                disabled={saving}
                              >
                                Anuluj
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {chimney.id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {chimney.company_name || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {chimney.city || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {chimney.phone || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {chimney.email || '-'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              <div className="max-w-xs truncate" title={chimney.description}>
                                {chimney.description || '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => handleEdit(chimney)}
                                className="text-ik-orange hover:text-orange-600"
                              >
                                Edytuj
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-6 text-sm text-gray-600">
            <p>Liczba kominiarzy: <span className="font-semibold">{chimneys.length}</span></p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminPanel;
